import { useQuery, QueryResult } from '@apollo/client'
import {
  GET_KUDOS_RECEIVED_BY_ME,
  GET_KUDOS_FOR_EMPLOYEE,
  GET_KUDOS_FOR_EMPLOYEE_IN_APPRAISAL,
  GET_ALL_KUDOS,
  GET_KUDOS_GIVEN_BY_REPLYER,
  GET_ALL_ATTENTION_POINTS,
  GET_ATTENTION_POINTS_FOR_EMPLOYEE,
  ARE_KUDOS_VISIBLE_FOR_ME,
  GET_KUDOS_LEGAL_ENTITY_VISIBILITY_DATA,
  GET_KUDOS_COMPANY_COUNTRY_VISIBILITY_DATA,
} from './Queries'
import {
  GetKudosReceivedByMeQuery,
  GetKudosReceivedByMeQueryVariables,
  GetKudosForEmployeeQuery,
  GetKudosForEmployeeQueryVariables,
  GetKudosForEmployeeInAppraisalQuery,
  GetKudosForEmployeeInAppraisalQueryVariables,
  GetAllKudosQuery,
  GetAllKudosQueryVariables,
  KudosPaginationInput,
  KudosAppraisalInput,
  KudosByReplyerFilterInput,
  GetKudosGivenByReplyerQueryVariables,
  GetKudosGivenByReplyerQuery,
  GetAllAttentionPointsQuery,
  GetAllAttentionPointsQueryVariables,
  GetAttentionPointsForEmployeeQuery,
  GetAttentionPointsForEmployeeQueryVariables,
  GetKudosLegalEntityVisibilityDataQuery,
  GetKudosLegalEntityVisibilityDataQueryVariables,
  GetKudosCompanyCountryVisibilityDataQuery,
  GetKudosCompanyCountryVisibilityDataQueryVariables,
  AreKudosVisibleForMeQuery,
  AreKudosVisibleForMeQueryVariables,
} from '../../types'

export const useAreKudosVisibleForMe = (): QueryResult<AreKudosVisibleForMeQuery, AreKudosVisibleForMeQueryVariables> => {
  return useQuery<AreKudosVisibleForMeQuery, AreKudosVisibleForMeQueryVariables>(ARE_KUDOS_VISIBLE_FOR_ME, {
    fetchPolicy: 'network-only',
  })
}

export const useKudosReceivedByMe = (
  pagination: KudosPaginationInput,
  shouldSkip = false,
): QueryResult<GetKudosReceivedByMeQuery, GetKudosReceivedByMeQueryVariables> => {
  return useQuery<GetKudosReceivedByMeQuery, GetKudosReceivedByMeQueryVariables>(GET_KUDOS_RECEIVED_BY_ME, {
    variables: { pagination },
    fetchPolicy: 'network-only',
    skip: shouldSkip,
  })
}

export const useKudosForEmployee = (
  employeeId: string,
  pagination: KudosPaginationInput,
  employeeResigned: boolean,
  shouldSkip: boolean,
): QueryResult<GetKudosForEmployeeQuery, GetKudosForEmployeeQueryVariables> => {
  return useQuery<GetKudosForEmployeeQuery, GetKudosForEmployeeQueryVariables>(GET_KUDOS_FOR_EMPLOYEE, {
    variables: { employeeId, pagination, employeeResigned },
    fetchPolicy: 'network-only',
    skip: shouldSkip,
  })
}

export const useAttentionPointsForEmployee = (
  employeeId: string,
  pagination: KudosPaginationInput,
  employeeResigned: boolean,
  shouldSkip: boolean,
): QueryResult<GetAttentionPointsForEmployeeQuery, GetAttentionPointsForEmployeeQueryVariables> => {
  return useQuery<GetAttentionPointsForEmployeeQuery, GetAttentionPointsForEmployeeQueryVariables>(GET_ATTENTION_POINTS_FOR_EMPLOYEE, {
    variables: { employeeId, pagination, employeeResigned },
    fetchPolicy: 'network-only',
    skip: shouldSkip,
  })
}

export const useKudosForEmployeeInAppraisal = (
  employeeId: string,
  appraisalInput: KudosAppraisalInput,
  shouldSkip = false,
): QueryResult<GetKudosForEmployeeInAppraisalQuery, GetKudosForEmployeeInAppraisalQueryVariables> => {
  return useQuery<GetKudosForEmployeeInAppraisalQuery, GetKudosForEmployeeInAppraisalQueryVariables>(GET_KUDOS_FOR_EMPLOYEE_IN_APPRAISAL, {
    variables: { employeeId, appraisalInput },
    fetchPolicy: 'network-only',
    skip: shouldSkip,
  })
}

export const useAllKudos = (filters: GetAllKudosQueryVariables['filters']): QueryResult<GetAllKudosQuery, GetAllKudosQueryVariables> => {
  return useQuery<GetAllKudosQuery, GetAllKudosQueryVariables>(GET_ALL_KUDOS, {
    variables: { filters },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
}

export const useAllAttentionPoints = (
  filters: GetAllKudosQueryVariables['filters'],
): QueryResult<GetAllAttentionPointsQuery, GetAllAttentionPointsQueryVariables> => {
  return useQuery<GetAllAttentionPointsQuery, GetAllAttentionPointsQueryVariables>(GET_ALL_ATTENTION_POINTS, {
    variables: { filters },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
}

export const useKudosGivenByReplyer = (
  filters: KudosByReplyerFilterInput,
): QueryResult<GetKudosGivenByReplyerQuery, GetKudosGivenByReplyerQueryVariables> => {
  return useQuery<GetKudosGivenByReplyerQuery, GetKudosGivenByReplyerQueryVariables>(GET_KUDOS_GIVEN_BY_REPLYER, {
    variables: { filters },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
}

// ///////////
// SETTINGS
// ///////////

export const useKudosLegalEntityVisibility = (
  filters: GetKudosLegalEntityVisibilityDataQueryVariables['filters'],
): QueryResult<GetKudosLegalEntityVisibilityDataQuery, GetKudosLegalEntityVisibilityDataQueryVariables> => {
  return useQuery<GetKudosLegalEntityVisibilityDataQuery, GetKudosLegalEntityVisibilityDataQueryVariables>(
    GET_KUDOS_LEGAL_ENTITY_VISIBILITY_DATA,
    {
      variables: { filters },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  )
}

export const useKudosCompanyCountryVisibility = (
  filters: GetKudosCompanyCountryVisibilityDataQueryVariables['filters'],
): QueryResult<GetKudosCompanyCountryVisibilityDataQuery, GetKudosCompanyCountryVisibilityDataQueryVariables> => {
  return useQuery<GetKudosCompanyCountryVisibilityDataQuery, GetKudosCompanyCountryVisibilityDataQueryVariables>(
    GET_KUDOS_COMPANY_COUNTRY_VISIBILITY_DATA,
    {
      variables: { filters },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  )
}
